import { useEffect, useRef } from 'react';
import type { ModelInfo } from '../lib/api';
import { priceRows, sayBlurbBy, sayConstraints, sayDollars, sayPrice, sayPrivacy, sayRetirement, wholePrice } from '../lib/models';
import { useCreditBalance } from '../lib/credits';

// ---------------------------------------------------------------------------
// The info panel (205): everything the catalogue knows about one model, on
// one sheet. The blurb and who wrote it; every priced setting in credits
// and in money at this workspace's rate; what the model can do, in plain
// sentences from its constraints; the vendor's privacy word explained; and
// when it is going away. One button uses it, when the panel was opened
// from a bench. A sheet over the page, not a popover: it is read, not
// scanned, and it has to hold a table. Escape and the scrim close it.
// ---------------------------------------------------------------------------

const SHOT_SECONDS = 8;
const BED_SECONDS = 30;

export function ModelInfoPanel({ bench, model, onClose, onUse }: { bench: string; model: ModelInfo; onClose: () => void; onUse?: (m: ModelInfo) => void }) {
  const { credits } = useCreditBalance();
  const rate = credits?.plan.centsPer1000 ?? null;
  const closeBtn = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeBtn.current?.focus();
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [onClose]);

  const rows = priceRows(model);
  const facts = sayConstraints(bench, model);
  const privacy = sayPrivacy(model.privacy);
  const by = sayBlurbBy(model.blurbBy);
  const going = sayRetirement(model);
  const whole = (r: { credits: number | null; unit: string }) =>
    r.unit === 'second' ? wholePrice(r.credits, r.unit, bench === 'music' ? BED_SECONDS : SHOT_SECONDS) : null;

  return (
    <div className="sheet" role="presentation" onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <section className="sheet-inner" role="dialog" aria-modal="true" aria-labelledby="model-info-title" data-tool="model-info">
        <header>
          <div>
            <h3 id="model-info-title">{model.name}</h3>
            <p className="sub-dim" style={{ margin: 0 }}>
              {model.vendor} · <span title={privacy.means}>{privacy.word}</span>
              {model.isNew ? ' · new this fortnight' : ''}{model.featured ? ' · featured' : ''}{model.houseDefault ? ' · the house default' : ''}{model.workspaceDefault ? ' · your workspace\'s default' : ''}
            </p>
          </div>
          <button type="button" ref={closeBtn} className="btn tiny" onClick={onClose} aria-label="Close">Close</button>
        </header>
        {going && <p className="notice" role="status">{going}</p>}
        <p className="info-blurb">{model.blurb ?? 'The vendor has not described this model, and the house has not written about it yet.'}</p>
        {by && <p className="sub-dim info-by">{by}</p>}

        <h4>What it costs</h4>
        <div className="tablewrap">
          <table className="ledger info-prices">
            <thead><tr><th>Setting</th><th className="num">Credits</th>{rate ? <th className="num">At your rate</th> : null}</tr></thead>
            <tbody>
              {rows.map((r) => {
                const w = whole(r);
                return (
                  <tr key={r.label} className={r.default ? 'on' : ''}>
                    <td>{r.label}{r.default ? <span className="sub-dim"> · default</span> : null}</td>
                    <td className="num">{sayPrice(r.credits, r.unit)}{w !== null ? <span className="sub-dim"> · {w.toLocaleString()} a {bench === 'music' ? `${BED_SECONDS}-second bed` : `${SHOT_SECONDS}-second shot`}</span> : null}</td>
                    {rate ? <td className="num">{sayDollars(w ?? r.credits, rate) ?? '—'}</td> : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {rate ? <p className="sub-dim" style={{ margin: 0 }}>Money is what these credits cost at your plan's rate. The credits are what is charged.</p> : null}

        {facts.length > 0 && <>
          <h4>What it can do</h4>
          <ul className="info-facts">{facts.map((f) => <li key={f}>{f}</li>)}</ul>
        </>}

        <h4>Privacy</h4>
        <p style={{ margin: 0 }}><b>{privacy.word}.</b> {privacy.means}</p>

        {onUse && (
          <footer className="row" style={{ justifyContent: 'flex-end', marginTop: 6 }}>
            <button type="button" className="btn" data-move="model-use" onClick={() => { onUse(model); onClose(); }}>Use this model</button>
          </footer>
        )}
      </section>
    </div>
  );
}
